import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { disaggregatedMealToRow } from '@/lib/nutrientDistribution';
import { patientsApi, type CreatePatientInput, type PatchPatientInput, type Patient } from '@/api/patients';
import { hospitalApi, type MovePatientClinicInput } from '@/api/hospital';
import {
  mealsApi,
  type CreateMealInput,
  type MealItemInput,
  type MealListParams,
  type MealsTableParams,
  type UpdateMealInput,
} from '@/api/meals';
import { foodsApi } from '@/api/foods';
import type { ChangeContext } from '@/lib/changeContext';
import { nutrientsApi } from '@/api/nutrients';
import { goalsApi } from '@/api/goals';
import { recipesApi, type RecipeInput } from '@/api/recipes';
import {
  openaiApi,
  type OpenAiVariant,
  type OpenAIChatCompletionRequest,
} from '@/api/openai';
import { mealPlansApi } from '@/api/mealPlans';
import { emotionsApi } from '@/api/emotions';
import { dashboardApi } from '@/api/dashboard';
import { reportsApi } from '@/api/reports';
import { patientNotesApi } from '@/api/patientNotes';
import { patientDocumentsApi, type PatchPatientDocumentInput } from '@/api/patientDocuments';
import { waterApi, type WaterEntry } from '@/api/water';
import { resolveLanguageCode } from '@/i18n/languages';
import { userSurveysApi } from '@/api/userSurveys';
import {
  clinicianTipsApi,
  type CreateClinicianTipBatchRequest,
} from '@/api/clinicianTips';
import {
  clinicianLinksApi,
  type CreateClinicianLinkBatchRequest,
} from '@/api/clinicianLinks';
import {
  telemetryApi,
  type ClinicianMealFeedbackInput,
  type ClinicianUiFeedbackInput,
} from '@/api/telemetry';
import { metricsApi } from '@/api/metrics';
import {
  subscriptionsApi,
  type CreateSubscriptionInput,
  type UpdateSubscriptionInput,
} from '@/api/subscriptions';
import { cgmApi } from '@/api/cgm';
import { usersApi, type CurrentUser } from '@/api/users';
import { cliniciansApi } from '@/api/clinicians';
import { internalUserApi } from '@/api/internalUser';
import { patientAppDataApi } from '@/api/patientAppData';
import { activityFeedApi } from '@/api/activityFeed';
import { advisorChatApi } from '@/api/advisorChat';
import {
  resolveGlucoseTargets,
  toStored,
  type GlucoseTargets,
  type StoredGlucoseRanges,
} from '@/lib/glucoseRanges';

export const queryKeys = {
  currentUser: ['currentUser'] as const,
  currentPatient: ['patient', 'current'] as const,
  patientList: (clinicianId: number) => ['patients', 'list', clinicianId] as const,
  patient: (id: number) => ['patient', id] as const,
  meals: (patientId: number, params?: MealListParams) =>
    ['patient', patientId, 'meals', params?.from ?? null, params?.to ?? null] as const,
  mealsTable: (patientId: number, params?: MealsTableParams) =>
    ['patient', patientId, 'meals_table', params?.from ?? null, params?.to ?? null, params?.limit ?? null, params?.offset ?? null] as const,
  meal: (patientId: number, mealId: number) => ['patient', patientId, 'meal', mealId] as const,
  nutrients: (patientId: number) => ['patient', patientId, 'nutrients'] as const,
  carePlanValues: (patientId: number) => ['patient', patientId, 'care-plan-values'] as const,
  recipes: (patientId: number) => ['patient', patientId, 'recipes'] as const,
  managedRecipes: (patientId: number, search = '') =>
    ['patient', patientId, 'managed-recipes', search] as const,
  mealPlans: (patientId: number) => ['patient', patientId, 'meal-plans'] as const,
  emotions: (patientId: number) => ['patient', patientId, 'emotions'] as const,
  dashboardWeight: (patientId: number) => ['patient', patientId, 'weight'] as const,
  dashboardBP: (patientId: number) => ['patient', patientId, 'bp'] as const,
  dashboardExercise: (patientId: number) => ['patient', patientId, 'exercise'] as const,
  reportsMetrics: (patientId: number) => ['patient', patientId, 'reports', 'metrics'] as const,
  subscriptions: (patientId: number) => ['patient', patientId, 'subscriptions'] as const,
  cgmDays: (patientId: number) => ['patient', patientId, 'cgm', 'days'] as const,
  patientNotes: (patientId: number) => ['patient', patientId, 'notes'] as const,
  patientDocuments: (patientId: number) => ['patient', patientId, 'documents'] as const,
};

type Opts<T> = Omit<UseQueryOptions<T, unknown, T>, 'queryKey' | 'queryFn'>;

export const useCurrentUser = (opts?: Opts<CurrentUser>) =>
  useQuery<CurrentUser, unknown, CurrentUser>({
    queryKey: queryKeys.currentUser,
    queryFn: () => usersApi.current(),
    ...opts,
  });

export const useCurrentClinician = () =>
  useQuery({
    queryKey: ['clinicians', 'current'] as const,
    queryFn: () => cliniciansApi.current(),
  });

/**
 * The authenticated patient's own record (`GET /patients/current`). Errors
 * (400) for a non-patient token — callers that only want the identity probe
 * should pass `retry: false` and inspect the error, as `useResolvedRole` does.
 */
export const useCurrentPatient = (opts?: Opts<Patient>) =>
  useQuery<Patient, unknown, Patient>({
    queryKey: queryKeys.currentPatient,
    queryFn: () => patientsApi.current(),
    ...opts,
  });

/**
 * The caller's RxFood internal-staff membership and access tier.
 *
 * Safe to call for everyone: the endpoint answers 200 with
 * `is_internal_user: false` for an ordinary clinician rather than 403.
 * Long `staleTime` because a staff tier does not change mid-session, and
 * several surfaces gate on it.
 */
export const useInternalUser = () =>
  useQuery({
    queryKey: ['internal-user', 'me'] as const,
    queryFn: () => internalUserApi.me(),
    staleTime: 5 * 60_000,
  });

/**
 * Mutation for `PATCH /hospitals/{h}/clinicians/{c}`. Used by the language
 * switcher today; the endpoint also covers other clinician profile fields.
 * Invalidates the `current clinician` query so dependent UI re-reads.
 */
export const useUpdateClinician = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      hospitalId,
      clinicianId,
      body,
    }: {
      hospitalId: number;
      clinicianId: number;
      body: import('@/api/clinicians').PatchClinicianBody;
    }) => cliniciansApi.patch(hospitalId, clinicianId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clinicians', 'current'] });
    },
  });
};

/**
 * All clinicians registered in the hospital. Used by the Clinicians
 * management page.
 */
export const useHospitalClinicians = (hospitalId: number | null | undefined) =>
  useQuery({
    queryKey: ['hospital', hospitalId ?? 0, 'clinicians'] as const,
    queryFn: () => cliniciansApi.listByHospital(hospitalId!),
    enabled: !!hospitalId,
  });

/**
 * Create a new clinician in a hospital. Invalidates the hospital
 * clinicians list so the Clinicians page picks up the new row.
 */
export const useCreateClinician = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      hospitalId,
      body,
    }: {
      hospitalId: number;
      body: import('@/api/clinicians').CreateClinicianBody;
    }) => cliniciansApi.create(hospitalId, body),
    onSuccess: (_data, args) => {
      qc.invalidateQueries({ queryKey: ['hospital', args.hospitalId, 'clinicians'] });
    },
  });
};

/**
 * Patient-list flag-icon mutations: resend invite / revoke invite / deregister
 * patient. All three invalidate the patient-list queries on success so the
 * row state (status badge, icon colors) reflects the new server state.
 */
export const useResendInvitationEmail = (hospitalId: number | null | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { patientId: number; invitationId?: number | null }) =>
      hospitalApi.resendInvitationEmail({
        hospitalId: hospitalId!,
        patientId: args.patientId,
        invitationId: args.invitationId,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['patients', 'list'] }),
  });
};

export const useRevokeClinicInvitation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: number) =>
      hospitalApi.updateClinicInvitation({ invitationId, status: 'revoked' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['patients', 'list'] }),
  });
};

export const useDeregisterPatient = (hospitalId: number | null | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { clinicId: number; patientId: number }) =>
      hospitalApi.deregisterPatient({
        hospitalId: hospitalId!,
        clinicId: args.clinicId,
        patientId: args.patientId,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['patients', 'list'] }),
  });
};

/** Move a patient between clinics in the same hospital (FOOD-651). Hospital
 *  is per-call so multi-hospital clinicians target the source clinic's hospital. */
export const useMovePatientClinic = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      hospitalId: number;
      patientId: number;
      body: MovePatientClinicInput;
    }) => hospitalApi.movePatientClinic(args),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['patients', 'list'] });
      qc.invalidateQueries({ queryKey: ['patient', vars.patientId, 'clinics'] });
    },
  });
};

export const usePatientList = (
  clinicianId: number | null | undefined,
  params?: {
    filter?: import('@/api/patients').PatientsTableFilter;
    limit?: number;
    offset?: string;
    order?: string;
  },
) => {
  // Stringify the filter into the queryKey so structurally-equal filters
  // (different object identities, same content) hit the same cache entry.
  const filterKey = params?.filter ? JSON.stringify(params.filter) : null;
  return useQuery({
    queryKey: [
      ...queryKeys.patientList(clinicianId ?? 0),
      filterKey,
      params?.limit ?? null,
      params?.offset ?? null,
      params?.order ?? null,
    ] as const,
    // Clinician-scoped, multi-hospital roster (FOOD-841) — no longer gated on a
    // single home hospital, so multi-hospital clinicians see all their patients.
    queryFn: () => patientsApi.listForClinician(clinicianId!, params),
    enabled: !!clinicianId,
    placeholderData: (prev) => prev,
    // Short passive-refresh — the roster should reflect newly-added patients
    // and status changes promptly, not up to 5 min later. (PR #66 review.)
    staleTime: 30_000,
  });
};

/**
 * Every clinic the clinician belongs to, across every hospital (FOOD-837) —
 * not scoped to a single hospital_id, since a clinician can now belong to
 * more than one (FOOD-559).
 */
export const useClinicianClinics = (clinicianId: number | null | undefined) =>
  useQuery({
    queryKey: ['clinics', clinicianId ?? 0] as const,
    queryFn: () => hospitalApi.allClinics(clinicianId!),
    enabled: !!clinicianId,
  });

/**
 * The clinic's default invitation code. The dialog opens with `enabled`
 * false until a clinic is selected so we don't pre-fetch codes for every
 * clinic on mount — the code value is sensitive enough that we only want
 * it loaded when the user actively asks for it. Cached for 5 minutes so
 * a clinician switching the selector back and forth doesn't refetch.
 */
export const useClinicInvitationCode = (
  hospitalId: number | null | undefined,
  clinicId: number | null | undefined,
  opts: { enabled?: boolean } = {},
) =>
  useQuery({
    queryKey: ['clinic', hospitalId ?? 0, clinicId ?? 0, 'invitation-code'] as const,
    queryFn: () =>
      hospitalApi.clinicInvitationCode({ hospitalId: hospitalId!, clinicId: clinicId! }),
    enabled: !!hospitalId && !!clinicId && (opts.enabled ?? true),
    staleTime: 5 * 60 * 1000,
  });

export const usePatientClinics = (patientId: number | null | undefined) =>
  useQuery({
    queryKey: ['patient', patientId ?? 0, 'clinics'] as const,
    queryFn: () => patientsApi.clinics(patientId!),
    enabled: !!patientId,
  });

/**
 * Patient medications log — see `patientsApi.medications` for the response
 * shape. Default range covers the last 180 days so the rail's "current
 * medications" derivation has enough rows to dedupe-by-name.
 */
export const usePatientMedications = (
  patientId: number | null | undefined,
  params?: { from?: string; to?: string },
) =>
  useQuery({
    queryKey: [
      'patient',
      patientId ?? 0,
      'medications',
      params?.from ?? null,
      params?.to ?? null,
    ] as const,
    queryFn: () => patientsApi.medications(patientId!, params),
    enabled: !!patientId,
  });

/**
 * Hospital details (mainly for the human-readable name used as a section
 * header in the clinic filter dropdown). Long staleTime — hospital names
 * essentially never change during a session.
 */
export const useHospital = (hospitalId: number | null | undefined) =>
  useQuery({
    queryKey: ['hospital', hospitalId ?? 0] as const,
    queryFn: () => hospitalApi.get(hospitalId!),
    enabled: !!hospitalId,
    staleTime: 60 * 60 * 1000,
  });

export const useCreatePatient = (clinicianId: number | null | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePatientInput) => patientsApi.create(input),
    onSuccess: () => {
      if (clinicianId) {
        qc.invalidateQueries({ queryKey: queryKeys.patientList(clinicianId) });
      }
    },
  });
};

export const usePatient = (patientId: number | null) =>
  useQuery({
    queryKey: queryKeys.patient(patientId ?? 0),
    queryFn: () => patientsApi.get(patientId!),
    enabled: patientId !== null,
  });

export const useUpdatePatient = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchPatientInput) => patientsApi.update(patientId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.patient(patientId) });
      // The patient table also reflects names/email — invalidate any list
      // queries so they refresh.
      qc.invalidateQueries({ queryKey: ['patients', 'list'] });
    },
  });
};

export const useMeals = (patientId: number, params?: MealListParams) =>
  useQuery({
    queryKey: queryKeys.meals(patientId, params),
    queryFn: () => mealsApi.list(patientId, params),
    // Keep meal data on a short passive-refresh window — the patient may be
    // logging in real time while a clinician watches. The global gcTime still
    // keeps it cached, so revisits show instantly (no skeleton); this only
    // shortens the background refetch. (PR #66 review.)
    staleTime: 30_000,
  });

/**
 * Distinct dates the patient has logged meals on since the last report
 * was sent. Drives the Generate-Report dialog's "since last report" hint
 * and the auto-fill of the initial date range.
 */
export const useMealsSinceLastReport = (patientId: number | null | undefined) =>
  useQuery({
    queryKey: ['patient', patientId ?? 0, 'meals-since-last-report'] as const,
    queryFn: () => mealsApi.sinceLastReport(patientId!),
    enabled: !!patientId,
    // Short passive-refresh — feeds the last-meal-date smart default. (PR #66 review.)
    staleTime: 30_000,
  });

/**
 * Patient's most recent meal date. Returns `null` until data resolves, then
 * the latest YYYY-MM-DD on file.
 *
 * Three-source lookup, in priority order:
 *
 *   1. `patient.last_meal_date` — direct field, only populated by some
 *      server envs (the documented `PatientResponse` schema, swagger:14223,
 *      doesn't include it, but `ClinicianPatientsTableResponseRow` at
 *      swagger:18746 does — some installations carry it on the single-
 *      patient GET too). Free if the server returns it.
 *
 *   2. `meals_since_last_report` — every date with at least one meal
 *      since the last SENT report. Empty right after a report is sent
 *      and stays empty until the patient logs again. So it can't be the
 *      only source — but it's the cheapest one when it has data.
 *
 *   3. Activity feed (past 90 days) — the canonical, always-correct
 *      source. Mirrors v1's `useCalendarActivityIndicators.newestDate`
 *      (clinician/src/components/useCalendarActivityFeed.ts:62). Scans
 *      for the newest `meal-log` row in the past 90 days. Only fires if
 *      the cheaper sources came up empty.
 *
 * The previous implementation stopped at source 2, which meant the
 * "Last meal" chip and the Quick Select "Latest data" group disappeared
 * immediately after a clinician sent a report — exactly when the
 * clinician was likely to be back in the report dialog looking at it.
 */
export const useLastMealDate = (
  patientId: number | null | undefined,
): { date: string | null; isLoading: boolean } => {
  const mealsSince = useMealsSinceLastReport(patientId);
  const patient = usePatient(patientId ?? null);

  // Source 1.
  const fromPatient = patient.data?.last_meal_date ?? null;

  // Source 2.
  const sinceDates = mealsSince.data?.meal_dates ?? [];
  const fromSince = sinceDates.length > 0
    ? [...sinceDates].sort()[sinceDates.length - 1]
    : null;

  // Only fetch the activity feed when sources 1 + 2 came up empty. The
  // 90-day window is large enough to cover any clinically-relevant gap
  // between reports while staying under the activity feed's 500-row cap
  // (5 entries/day × 90 days = 450).
  const needsFallback = !fromPatient && !fromSince && !mealsSince.isLoading;
  const activityRange = useMemo(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 90);
    const fmt = (d: Date) => d.toISOString().slice(0, 10);
    return { from: fmt(from), to: fmt(to) };
  }, []);
  const activity = useQuery({
    queryKey: ['patient', patientId ?? 0, 'last-meal-fallback', activityRange.from, activityRange.to] as const,
    queryFn: () => activityFeedApi.list(patientId!, { date_since: activityRange.from, date_until: activityRange.to, limit: 500 }),
    enabled: !!patientId && needsFallback,
  });

  if (fromPatient) {
    return { date: fromPatient.slice(0, 10), isLoading: false };
  }
  if (fromSince) {
    return { date: fromSince, isLoading: false };
  }
  // Source 3 — scan the (descending) feed for the first meal-log.
  const feedRows = activity.data?.rows ?? [];
  for (const row of feedRows) {
    if (row.type === 'meal-log') {
      return { date: row.timestamp.slice(0, 10), isLoading: false };
    }
  }

  return {
    date: null,
    isLoading:
      mealsSince.isLoading || patient.isLoading || (needsFallback && activity.isLoading),
  };
};

export const useMealsTable = (patientId: number, params?: MealsTableParams) =>
  useQuery({
    queryKey: queryKeys.mealsTable(patientId, params),
    queryFn: () => mealsApi.table(patientId, params),
    // Short passive-refresh — the meal-logs table should reflect new logs
    // promptly while a clinician reviews. (PR #66 review.)
    staleTime: 30_000,
  });

export const useMeal = (patientId: number, mealId: number | null) =>
  useQuery({
    queryKey: queryKeys.meal(patientId, mealId ?? 0),
    queryFn: () => mealsApi.get(patientId, mealId!),
    enabled: mealId !== null,
  });

export const useDeleteMeal = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { mealId: number; changeCtx?: ChangeContext }) =>
      mealsApi.deleteMeal(patientId, args.mealId, args.changeCtx),
    onSuccess: () => invalidateMealsAfterMutation(qc, patientId),
  });
};

export const useDeleteMealItem = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { mealId: number; mealItemId: number; changeCtx?: ChangeContext }) =>
      mealsApi.deleteMealItem(patientId, args.mealId, args.mealItemId, args.changeCtx),
    onSuccess: (_data, vars) => invalidateMealsAfterMutation(qc, patientId, vars.mealId),
  });
};

// Also invalidate Logs activity-feed / nutrition-details — those surfaces embed
// meal_items and stay stale for up to staleTime (5m) without this.
const invalidateMealsAfterMutation = (
  qc: ReturnType<typeof useQueryClient>,
  patientId: number,
  mealId?: number,
) => {
  if (mealId) qc.invalidateQueries({ queryKey: queryKeys.meal(patientId, mealId) });
  qc.invalidateQueries({ queryKey: ['patient', patientId, 'meals'] });
  qc.invalidateQueries({ queryKey: ['patient', patientId, 'meals_table'] });
  qc.invalidateQueries({ queryKey: ['patient', patientId, 'activity-feed'] });
  qc.invalidateQueries({ queryKey: ['patient', patientId, 'nutrition-details'] });
};

export const useCreateMeal = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMealInput) => mealsApi.create(patientId, body),
    onSuccess: () => invalidateMealsAfterMutation(qc, patientId),
  });
};

export const useUpdateMeal = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { mealId: number; body: UpdateMealInput }) =>
      mealsApi.update(patientId, args.mealId, args.body),
    onSuccess: (_data, vars) => invalidateMealsAfterMutation(qc, patientId, vars.mealId),
  });
};

export const useAddMealItems = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { mealId: number; items: MealItemInput[] }) =>
      mealsApi.addItems(patientId, args.mealId, args.items),
    onSuccess: (_data, vars) => invalidateMealsAfterMutation(qc, patientId, vars.mealId),
  });
};

export const useUpdateMealItem = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { mealId: number; mealItemId: number; body: MealItemInput }) =>
      mealsApi.updateItem(patientId, args.mealId, args.mealItemId, args.body),
    onSuccess: (_data, vars) => invalidateMealsAfterMutation(qc, patientId, vars.mealId),
  });
};

// Defensively accept `undefined` — the consumers in MealItemFormDialog can
// briefly hand us `undefined` if they re-init from a meal item with no
// `food_name`. Treating it as empty keeps the form from crashing.
export const useFoodAutocomplete = (
  partialTerm: string | undefined,
  opts?: {
    contextUserId?: number;
    contextMealName?: string | null;
    contextAddonOf?: string | null;
  },
) => {
  const { i18n } = useTranslation();
  const term = partialTerm ?? '';
  // Backend context_lang only branches on `fr`; normalize en-US / fr-CA etc.
  const lang = resolveLanguageCode(i18n.language);
  const contextUserId = opts?.contextUserId;
  const contextMealName = opts?.contextMealName ?? null;
  const contextAddonOf = opts?.contextAddonOf ?? null;
  return useQuery({
    queryKey: [
      'foods',
      'search_v2_interactive',
      term,
      contextUserId ?? null,
      lang ?? null,
      contextMealName,
      contextAddonOf,
    ] as const,
    queryFn: () =>
      foodsApi.autocomplete(term, {
        contextUserId,
        contextLang: lang,
        contextMealName,
        contextAddonOf,
      }),
    enabled: term.trim().length >= 2,
    staleTime: 60_000,
  });
};

export const useCarePlanValues = (patientId: number, params?: { from?: string; to?: string }) =>
  useQuery({
    queryKey: [...queryKeys.carePlanValues(patientId), params?.from ?? null, params?.to ?? null] as const,
    queryFn: () => nutrientsApi.carePlanValues(patientId, params),
  });

/**
 * Recommended Daily Intake band keyed on the patient's profile. Stable for
 * the patient's lifetime in practice (it's based on age/sex/country and
 * doesn't drift day-to-day), so we lean on a long staleTime.
 */
export const useRdi = (patientId: number) =>
  useQuery({
    queryKey: ['patient', patientId, 'rdi'] as const,
    queryFn: () => nutrientsApi.rdi(patientId),
    staleTime: 60 * 60 * 1000,
  });

export const useGoalTargets = () =>
  useQuery({
    queryKey: ['goal-targets'] as const,
    queryFn: () => goalsApi.targets(),
    // Catalog rarely changes — keep a long cache.
    staleTime: 60 * 60 * 1000,
  });

/**
 * Range-based goal progress. Best-effort — the `progress_report` endpoint
 * is currently 500-ing for several patient/range combinations on prod
 * (tracked as a backend bug). We swallow 4xx/5xx and return an empty list
 * so the Goals tab keeps rendering (the snapshot from `useAllGoals` is the
 * row list). When the backend is fixed, this will start populating range
 * stats automatically with no V2 changes.
 */
export const useGoals = (patientId: number, range: { from: string; to: string }) =>
  useQuery({
    queryKey: ['patient', patientId, 'goals', 'list', range.from, range.to] as const,
    queryFn: async () => {
      try {
        return await goalsApi.list(patientId, range.from, range.to);
      } catch (err) {
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status === undefined || status >= 400) return [];
        throw err;
      }
    },
    retry: false,
  });

/**
 * Canonical goal list — always returns every active goal (snapshot for
 * today's date), independent of any user-selected date range. This is the
 * source of truth for "which goals exist"; pair with `useGoals(range)` to
 * overlay per-range progress stats by `goal_id`.
 *
 * Switched from `progress_report` to the snapshot endpoint because the
 * range endpoint occasionally 500s server-side, leaving the tab blank.
 * The snapshot endpoint is much simpler server-side and reliably returns
 * the active goal list.
 */
export const useAllGoals = (patientId: number) => {
  const today = new Date().toISOString().slice(0, 10);
  return useQuery({
    queryKey: ['patient', patientId, 'goals', 'snapshot', today] as const,
    queryFn: () => goalsApi.snapshot(patientId, today),
  });
};

export const useCreateGoal = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: import('@/api/goals').GoalInput) => goalsApi.create(patientId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['patient', patientId, 'goals'] }),
  });
};

export const useUpdateGoal = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ goalId, body }: { goalId: number; body: import('@/api/goals').GoalInput }) =>
      goalsApi.update(patientId, goalId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['patient', patientId, 'goals'] }),
  });
};

export const useDeleteGoal = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (goalId: number) => goalsApi.remove(patientId, goalId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['patient', patientId, 'goals'] }),
  });
};

export const useRecipes = (patientId: number, enabled = true) =>
  useQuery({
    queryKey: queryKeys.recipes(patientId),
    queryFn: () => recipesApi.list(patientId),
    enabled,
  });

export const useManagedRecipes = (patientId: number, search = '', enabled = true) =>
  useQuery({
    queryKey: queryKeys.managedRecipes(patientId, search),
    queryFn: () => recipesApi.listManaged(patientId, search),
    enabled,
  });

export const useManagedRecipe = (
  patientId: number,
  recipeId: number | null,
  enabled = true,
) =>
  useQuery({
    queryKey: ['patient', patientId, 'managed-recipes', recipeId] as const,
    queryFn: () => recipesApi.getManaged(patientId, recipeId!),
    enabled: enabled && recipeId != null,
  });

export const useCopyManagedRecipe = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (recipeId: number) => recipesApi.copyManaged(patientId, recipeId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patient', patientId, 'managed-recipes'] });
    },
  });
};

export const useUpsertRecipe = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { recipeId?: number; body: RecipeInput }) =>
      args.recipeId !== undefined
        ? recipesApi.update(patientId, args.recipeId, args.body)
        : recipesApi.create(patientId, args.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.recipes(patientId) }),
  });
};

export const useDeleteRecipe = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (recipeId: number) => recipesApi.deleteRecipe(patientId, recipeId),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.recipes(patientId) }),
  });
};

/**
 * Edamam recipe search — driven once per generated recipe name in the
 * recipe-generator result modal. Cached by (name, filters) so re-rendering
 * the modal doesn't re-fetch.
 */
export const useRecipeSearch = (
  name: string | null | undefined,
  filtersJson?: string,
) =>
  useQuery({
    queryKey: ['recipe-search', name ?? '', filtersJson ?? ''] as const,
    queryFn: () => recipesApi.search(name!, filtersJson),
    enabled: !!name,
  });

/**
 * Save a recipe to a patient's recipe list (defaults to `patient_favourites`,
 * which is the inbox the patient sees in their app). Invalidates the
 * Recipes-tab list so the saved-recipes table refreshes after save.
 */
export const useAddRecipeToList = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      listIdOrType?: import('@/api/recipes').PatientRecipeListType | string;
      recipeId: number;
    }) =>
      recipesApi.addItem(
        patientId,
        args.listIdOrType ?? 'patient_favourites',
        args.recipeId,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.recipes(patientId) }),
  });
};

/**
 * Single mutation hook used by the recipe generator. Variant is supplied per
 * call (rather than baked into the hook) so the same component can switch
 * between recipes / meal-plan / food-substitutions without remounting — and
 * the result lives on `mutation.data` until the user clears it.
 */
export const useGenerateRecipeContent = () =>
  useMutation({
    mutationFn: (args: { variant: OpenAiVariant; body: OpenAIChatCompletionRequest }) =>
      openaiApi.chat(args.variant, args.body),
  });

/**
 * Patient meal-plan list. Polls every 3s while ANY plan is `generating`,
 * matching v1's behaviour (clinician/src/pages/patient-meal-plans.tsx:89).
 * Once generation settles the polling stops automatically.
 */
export const useMealPlans = (patientId: number) =>
  useQuery({
    queryKey: queryKeys.mealPlans(patientId),
    queryFn: () => mealPlansApi.list(patientId),
    refetchInterval: (q) => {
      const data = q.state.data;
      const generating = data?.some((p) => p.status === 'generating');
      return generating ? 3000 : false;
    },
  });

/**
 * Single meal-plan detail (days/slots tree). Same 3s poll as the list while
 * status is `generating`, matching v1's viewer
 * (clinician/src/pages/patient-meal-plan-viewer.tsx:600).
 */
export const useMealPlan = (
  patientId: number,
  planId: number | null | undefined,
) =>
  useQuery({
    queryKey: ['patient', patientId, 'meal-plan', planId ?? 0] as const,
    queryFn: () => mealPlansApi.get(patientId, planId!),
    enabled: !!planId,
    refetchInterval: (q) =>
      q.state.data?.status === 'generating' ? 3000 : false,
  });

/* -------------------- TDEE -------------------- */

/**
 * TDEE calculator. Stateless — no cache, just a mutation. v1 debounces this
 * 600ms in the intake form; that's the form's responsibility, not the hook's.
 */
export const useTdeeCalculate = (patientId: number) =>
  useMutation({
    mutationFn: (body: import('@/api/mealPlans').TdeeCalculateInput) =>
      mealPlansApi.tdeeCalculate(patientId, body),
  });

/* -------------------- CREATE / DELETE -------------------- */

/** Create a new meal plan from the intake form. */
export const useCreateMealPlan = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: import('@/api/mealPlans').CreateMealPlanInput) =>
      mealPlansApi.create(patientId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.mealPlans(patientId) }),
  });
};

export const useDeleteMealPlan = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planId: number) => mealPlansApi.deletePlan(patientId, planId),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.mealPlans(patientId) }),
  });
};

/* -------------------- LIFECYCLE -------------------- */

const invalidatePlan = (qc: ReturnType<typeof useQueryClient>, patientId: number, planId: number) => {
  qc.invalidateQueries({ queryKey: ['patient', patientId, 'meal-plan', planId] });
  qc.invalidateQueries({ queryKey: queryKeys.mealPlans(patientId) });
};

export const useGenerateMealPlan = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planId: number) => mealPlansApi.generate(patientId, planId),
    onSuccess: (_data, planId) => invalidatePlan(qc, patientId, planId),
  });
};

export const useFinalizeMealPlan = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planId: number) => mealPlansApi.finalize(patientId, planId),
    onSuccess: (_data, planId) => invalidatePlan(qc, patientId, planId),
  });
};

export const useDuplicateMealPlan = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planId: number) => mealPlansApi.duplicate(patientId, planId),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.mealPlans(patientId) }),
  });
};

/* -------------------- SWAP -------------------- */

/** Per-slot recipe-swap suggestion fetch. Result lives on `mutation.data`
 *  until a confirm or a fresh search clears it — that's how the swap dialog
 *  drives its multi-step flow. */
export const useMealPlanSwapSuggestions = (patientId: number, planId: number) =>
  useMutation({
    mutationFn: (body: import('@/api/mealPlans').SwapSuggestionsInput) =>
      mealPlansApi.swapSuggestions(patientId, planId, body),
  });

export const useMealPlanSwapConfirm = (patientId: number, planId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: import('@/api/mealPlans').SwapConfirmInput) =>
      mealPlansApi.swapConfirm(patientId, planId, body),
    onSuccess: () => invalidatePlan(qc, patientId, planId),
  });
};

export const useMealPlanSwapIngredient = (patientId: number, planId: number) =>
  useMutation({
    mutationFn: (body: import('@/api/mealPlans').SwapIngredientInput) =>
      mealPlansApi.swapIngredient(patientId, planId, body),
  });

export const useMealPlanConfirmIngredientSwap = (patientId: number, planId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: import('@/api/mealPlans').ConfirmIngredientSwapInput) =>
      mealPlansApi.confirmIngredientSwap(patientId, planId, body),
    onSuccess: () => invalidatePlan(qc, patientId, planId),
  });
};

/* -------------------- GROCERY / SLOT / PDF / EMAIL -------------------- */

/** Generate-on-demand grocery list. Mutation rather than query so the
 *  dialog explicitly opts in (and can re-trigger after a recipe swap). */
export const useMealPlanGroceryList = (patientId: number, planId: number) =>
  useMutation({
    mutationFn: () => mealPlansApi.groceryList(patientId, planId),
  });

/** Lazy-fetched recipe instructions for one slot. v1 only fetches when the
 *  user expands the "View Instructions" panel and caches per slot. */
export const useSlotInstructions = (patientId: number, planId: number) =>
  useMutation({
    mutationFn: (slotId: number) => mealPlansApi.slotInstructions(patientId, planId, slotId),
  });

export const useSaveSlotNote = (patientId: number, planId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { slotId: number; note: string }) =>
      mealPlansApi.saveSlotNote(patientId, planId, args.slotId, args.note),
    onSuccess: () => invalidatePlan(qc, patientId, planId),
  });
};

/** Download PDF as a blob. Caller owns the resulting object URL — they
 *  trigger an `<a download>` click and revoke the URL afterward. */
export const useDownloadMealPlanPdf = (patientId: number) =>
  useMutation({
    mutationFn: (planId: number) => mealPlansApi.pdf(patientId, planId),
  });

export const useEmailMealPlan = (patientId: number) =>
  useMutation({
    mutationFn: (args: {
      planId: number;
      body?: import('@/api/mealPlans').EmailMealPlanInput;
    }) => mealPlansApi.email(patientId, args.planId, args.body ?? {}),
  });

/* -------------------- CLINIC SETTINGS -------------------- */

export const useClinicMealPlanSettings = (clinicId: number | null | undefined) =>
  useQuery({
    queryKey: ['clinic', clinicId ?? 0, 'meal-plan-settings'] as const,
    queryFn: () => mealPlansApi.clinicSettings(clinicId!),
    enabled: !!clinicId,
  });

export const useUpdateClinicMealPlanSettings = (clinicId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: import('@/api/mealPlans').ClinicMealPlanSettingsInput) =>
      mealPlansApi.updateClinicSettings(clinicId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clinic', clinicId, 'meal-plan-settings'] }),
  });
};

type DateParams = { from?: string; to?: string };

export const useEmotions = (patientId: number, params?: DateParams) =>
  useQuery({
    queryKey: [...queryKeys.emotions(patientId), params?.from ?? null, params?.to ?? null] as const,
    queryFn: () => emotionsApi.list(patientId, params),
  });

/**
 * Patient-customized question for the emotion tracker. Long stale time
 * since the question rarely changes during a session. Used to decide
 * whether to label the tab as "Emotion" or "Digestion" etc.
 */
export const useEmotionQuestion = (patientId: number) =>
  useQuery({
    queryKey: ['patient', patientId, 'emotion-question'] as const,
    queryFn: () => emotionsApi.question(patientId),
    staleTime: 60 * 60 * 1000,
  });

export const useWeight = (patientId: number, params?: DateParams) =>
  useQuery({
    queryKey: [...queryKeys.dashboardWeight(patientId), params?.from ?? null, params?.to ?? null] as const,
    queryFn: () => dashboardApi.weight(patientId, params),
  });

export const useBloodPressure = (patientId: number, params?: DateParams) =>
  useQuery({
    queryKey: [...queryKeys.dashboardBP(patientId), params?.from ?? null, params?.to ?? null] as const,
    queryFn: () => dashboardApi.bloodPressure(patientId, params),
  });

export const useExercises = (patientId: number, params?: DateParams) =>
  useQuery({
    queryKey: [...queryKeys.dashboardExercise(patientId), params?.from ?? null, params?.to ?? null] as const,
    queryFn: () => dashboardApi.exercises(patientId, params),
  });

export const useAddWeight = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof dashboardApi.addWeight>[1]) =>
      dashboardApi.addWeight(patientId, body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.dashboardWeight(patientId) }),
  });
};

export const useAddBloodPressure = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof dashboardApi.addBloodPressure>[1]) =>
      dashboardApi.addBloodPressure(patientId, body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.dashboardBP(patientId) }),
  });
};

export const useAddExercise = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof dashboardApi.addExercise>[1]) =>
      dashboardApi.addExercise(patientId, body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.dashboardExercise(patientId) }),
  });
};

export const useReportMetrics = (patientId: number, params: { from?: string; to?: string }) =>
  useQuery({
    queryKey: [...queryKeys.reportsMetrics(patientId), params.from ?? null, params.to ?? null] as const,
    queryFn: () =>
      reportsApi.metrics({
        date_since: params.from ?? '1970-01-01',
        date_until: params.to ?? new Date().toISOString().slice(0, 10),
      }),
    enabled: !!params.from && !!params.to,
  });

/**
 * Per-meal food-group breakdown for the Average Plate visual.
 */
export const useAveragePlate = (patientId: number, range: { from: string; to: string }) =>
  useQuery({
    queryKey: ['patient', patientId, 'average-plate', range.from, range.to] as const,
    queryFn: () => reportsApi.averagePlate(patientId, { date_since: range.from, date_until: range.to }),
    enabled: !!range.from && !!range.to,
  });

/**
 * Aggregate Mediterranean diet score for the date range — total + the
 * per-component subscores used for the linear progress bars on Summary.
 */
export const useMediterraneanScore = (patientId: number, range: { from: string; to: string }) =>
  useQuery({
    queryKey: ['patient', patientId, 'mediterranean', range.from, range.to] as const,
    queryFn: () => metricsApi.mediterranean(patientId, { date_since: range.from, date_until: range.to }),
    enabled: !!range.from && !!range.to,
  });

/**
 * Unified activity feed for the patient timeline (Logs v2). Returns all
 * server-provided log types for the date range — meals, exercise,
 * medications, sleep, steps, emotion, mindfulness, stress, emotional
 * health — keyed by row `type` for the renderer to switch on.
 */
export const useActivityFeed = (
  patientId: number,
  range: { from: string; to: string },
) =>
  useQuery({
    queryKey: ['patient', patientId, 'activity-feed', range.from, range.to] as const,
    queryFn: () => activityFeedApi.list(patientId, { date_since: range.from, date_until: range.to, limit: 500 }),
    enabled: !!range.from && !!range.to,
  });

/**
 * Per-day macronutrient totals (carbs / fat / protein / energy / fiber)
 * by meal_date. Drives the day-header macro donut on Logs v2.
 */
export const useNutritionDetailsByDate = (
  patientId: number,
  range: {
    from: string;
    to: string;
    /** Contiguous-range chunks, forwarded as the API's `meal_date.or` filter
     *  exactly as `useNutritionDetailsByMealName` does. OPT-IN: omit it and
     *  the query spans `from`..`to` as before, which is what the Generate
     *  Report dialog's calendar-dot fetch relies on. */
    dateRanges?: Array<[string, string]>;
  },
) =>
  useQuery({
    queryKey: [
      'patient',
      patientId,
      'nutrition-details',
      'meal_date',
      range.from,
      range.to,
      // In the key so toggling a day refetches rather than serving the
      // previous selection's rows.
      range.dateRanges?.map(([x, y]) => `${x}|${y}`).join(',') ?? null,
    ] as const,
    queryFn: () =>
      reportsApi.nutritionDetailsTable(patientId, {
        group_by: 'meal_date',
        date_since: range.from,
        date_until: range.to,
        date_ranges: range.dateRanges,
      }),
    enabled: !!range.from && !!range.to,
  });

export const useNutritionDetailsByMealName = (
  patientId: number,
  params: {
    from?: string;
    to?: string;
    /** Contiguous-range chunks. When present, takes priority over from/to and
     *  is forwarded to the API's `meal_date.or` filter — used by the Nutrients
     *  tab's per-day checkbox excluder. */
    dateRanges?: Array<[string, string]>;
  },
) =>
  useQuery({
    queryKey: [
      'patient',
      patientId,
      'nutrition-details',
      'meal_name',
      params.from ?? null,
      params.to ?? null,
      // Serialize ranges into the key so React Query refetches when the
      // clinician toggles individual days. Sorted-on-construction by caller.
      params.dateRanges?.map(([a, b]) => `${a}|${b}`).join(',') ?? null,
    ] as const,
    queryFn: () =>
      reportsApi.nutritionDetailsTable(patientId, {
        group_by: 'meal_name',
        date_since: params.from,
        date_until: params.to,
        date_ranges: params.dateRanges,
      }),
    enabled: !!params.from && !!params.to,
  });

/**
 * Per-meal nutrient breakdown (`group_by=meal_id`). Drives the Nutrients-tab
 * drilldown — one row per meal in the range carrying every nutrient's
 * sum/avg/etc. Heavier payload than the by-name/by-date variants, so the
 * hook stays disabled until a drilldown actually opens. v1's equivalent
 * fetch is `useNutrientDetailsData` (clinician/src/components/showNutrientDetails.tsx:51).
 */
export const useNutritionDetailsByMealId = (
  patientId: number,
  params: {
    from?: string;
    to?: string;
    dateRanges?: Array<[string, string]>;
    enabled?: boolean;
  },
) =>
  useQuery({
    queryKey: [
      'patient',
      patientId,
      'nutrition-details',
      'meal_id',
      params.from ?? null,
      params.to ?? null,
      params.dateRanges?.map(([a, b]) => `${a}|${b}`).join(',') ?? null,
    ] as const,
    queryFn: () =>
      // Multi-grouper so each row carries meal_id + meal_date + meal_name
      // without a second lookup. Without `meal_date,meal_name`, the rows
      // come back with only `meal_id`, and the drilldown filter dropped
      // every row trying to bucket them by date.
      reportsApi.nutritionDetailsTable(patientId, {
        group_by: 'meal_id,meal_date,meal_name',
        date_since: params.from,
        date_until: params.to,
        date_ranges: params.dateRanges,
      }),
    enabled: !!params.from && !!params.to && (params.enabled ?? true),
  });

/**
 * Disaggregated per-meal nutrition for the Summary distribution graphs. Fetches
 * each meal's disaggregated items (one request per meal — the API only exposes a
 * per-meal endpoint; each is Redis-cached server-side and cached individually by
 * React Query) and collapses them into per-meal `<field>_sum` rows.
 *
 * Uses disaggregated data (composites split into components) so the numbers
 * match what the PDF report computes, rather than raw meal-item nutrients.
 */
export const useDisaggregatedNutrition = (
  patientId: number,
  meals: Array<{ id: number }>,
  opts?: { enabled?: boolean },
) => {
  const enabled = opts?.enabled ?? true;
  const results = useQueries({
    queries: meals.map((m) => ({
      queryKey: ['patient', patientId, 'disaggregated-meal', m.id] as const,
      queryFn: () => mealsApi.disaggregatedMeal(patientId, m.id),
      enabled,
      // A meal's disaggregated nutrition doesn't change once logged, so cache
      // aggressively: stay fresh 30 min (no refetch on remount) and keep the
      // data in memory 60 min after the cards unmount. Combined with the
      // server-side Redis cache, navigating away and back to Summary is
      // instant; the heavy cost is only the first (cold) load.
      staleTime: 30 * 60 * 1000,
      gcTime: 60 * 60 * 1000,
    })),
  });

  const isLoading = enabled && results.some((r) => r.isLoading);
  // Re-derive rows only when the underlying data actually changes (react-query
  // keeps stable data references; dataUpdatedAt bumps on refetch).
  const signature = results.map((r) => r.dataUpdatedAt).join(',');
  const rows = useMemo(
    () =>
      results
        .map((r) => r.data)
        .filter((d): d is NonNullable<typeof d> => Boolean(d))
        .map(disaggregatedMealToRow),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signature],
  );

  return { rows, isLoading };
};

export const useReportSecureLinks = (patientId: number) =>
  useQuery({
    queryKey: ['patient', patientId, 'reports', 'secure-links'] as const,
    queryFn: () => reportsApi.secureLinks(patientId),
  });

/**
 * Full patient-report list. Wide date window matches v1's ±2-year fetch so
 * the Reports tab shows historical reports without requiring a date filter.
 */
export const usePatientReports = (
  patientId: number,
  opts: { enabled?: boolean } = {},
) => {
  // ±2 years from today is enough to cover any practical clinician view.
  const today = new Date();
  const since = new Date(today);
  since.setFullYear(today.getFullYear() - 2);
  const until = new Date(today);
  until.setFullYear(today.getFullYear() + 2);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  const dateSince = fmt(since);
  const dateUntil = fmt(until);
  return useQuery({
    queryKey: ['patient', patientId, 'reports', 'list', dateSince, dateUntil] as const,
    queryFn: () => reportsApi.list(patientId, dateSince, dateUntil),
    enabled: opts.enabled ?? true,
  });
};

/**
 * Fetch the report PDF as a Blob.
 *
 * Returns the Blob, NOT an object URL. The previous version cached a URL here
 * and claimed `gcTime` revoked it; it does not — TanStack has no "this entry
 * was evicted" hook, so evicting the entry freed the string and leaked the
 * blob, keeping a multi-MB PDF resident for the life of the page, once per
 * report previewed.
 *
 * The URL's lifetime belongs to whatever renders it, via `useObjectUrl`,
 * which revokes on unmount. Do not reintroduce a URL into this cache.
 * [FOOD-1594]
 */
export const useReportPdf = (reportId: number | null | undefined) =>
  useQuery({
    queryKey: ['report', 'pdf', reportId ?? 0] as const,
    queryFn: () => reportsApi.pdfBlob(reportId!),
    enabled: !!reportId,
    // The PDF is immutable for a given report — a long stale time saves a
    // refetch on tab focus.
    staleTime: 60 * 60 * 1000,
  });

export const useClinicalSummary = (
  patientId: number,
  params: { from?: string; to?: string },
) =>
  useQuery({
    queryKey: ['patient', patientId, 'clinical-summary', params.from ?? null, params.to ?? null] as const,
    queryFn: () =>
      reportsApi.clinicalSummary(patientId, {
        date_since: params.from!,
        date_until: params.to!,
        disaggregated: false,
      }),
    enabled: !!params.from && !!params.to,
  });

export const useGenerateReport = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    // Generation runs as a background task: the POST returns a task-poll link,
    // and the report PDF only exists once that task completes. Await the poll
    // here so the mutation stays `pending` (button shows "Generating…") until
    // the report is really ready — then invalidate so it appears. Refetching
    // immediately after the POST (the old behaviour) raced the task and showed
    // nothing.
    mutationFn: async (input: Omit<Parameters<typeof reportsApi.createDraft>[0], 'patientId'>) => {
      const res = (await reportsApi.createDraft({ patientId, ...input })) as { link?: string };
      if (res?.link) {
        await reportsApi.waitForReportTask(res.link);
      }
      return res;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patient', patientId, 'reports'] });
    },
  });
};

export const useSendReportLink = (patientId: number) =>
  useMutation({
    mutationFn: (reportId: number) => reportsApi.sendReportLink(patientId, reportId),
  });

export const useSubscriptions = (patientId: number) =>
  useQuery({ queryKey: queryKeys.subscriptions(patientId), queryFn: () => subscriptionsApi.list(patientId) });

export const useUpsertSubscription = (patientId: number, hospitalId: number | null | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args:
      | { id: number; body: UpdateSubscriptionInput }
      | { id?: undefined; body: CreateSubscriptionInput; hospitalId?: number }) => {
      if (args.id !== undefined) {
        if (!hospitalId) throw new Error('hospitalId required to update a subscription');
        return subscriptionsApi.update({
          hospitalId,
          patientId,
          subscriptionId: args.id,
          body: args.body,
        });
      }
      // Create uses the same hospital-prefixed path as update. Prefer a
      // caller-supplied hospital (the selected clinic's) over the clinician's
      // home hospital — v1 posts to clinic.hospital_id.
      const createHospitalId = args.hospitalId ?? hospitalId;
      if (!createHospitalId) throw new Error('hospitalId required to create a subscription');
      return subscriptionsApi.create({
        hospitalId: createHospitalId,
        patientId,
        body: args.body,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.subscriptions(patientId) }),
  });
};

export const useCancelSubscription = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { hospitalId: number; subscriptionId: number }) =>
      subscriptionsApi.cancel({
        hospitalId: args.hospitalId,
        patientId,
        subscriptionId: args.subscriptionId,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.subscriptions(patientId) }),
  });
};

/**
 * Flat list of CGM glucose readings within a date range. Pair with the
 * `GlucoseLineChart` for the Summary tab's CGM card.
 */
export const useCgmData = (patientId: number, params: { from?: string; to?: string }) =>
  useQuery({
    queryKey: ['patient', patientId, 'cgm', 'data', params.from ?? null, params.to ?? null] as const,
    queryFn: () => cgmApi.data(patientId, { from: params.from, to: params.to }),
    enabled: !!params.from && !!params.to,
  });

/**
 * Aggregated CGM whole-summary for a date range — drives the Glucose
 * Pattern Summary AGP chart, Glucose Statistics table, and Time in Range
 * bar on the Summary tab.
 */
export const useCgmWholeSummary = (
  patientId: number,
  range: { from: string; to: string },
) =>
  useQuery({
    queryKey: ['patient', patientId, 'cgm', 'whole-summary', range.from, range.to] as const,
    queryFn: () => cgmApi.wholeSummary(patientId, { date_since: range.from, date_until: range.to }),
    enabled: !!range.from && !!range.to,
  });

/** CGM devices the patient has used — populates the quick-select dropdown. */
export const useCgmDevices = (
  patientId: number,
  opts: { enabled?: boolean } = {},
) =>
  useQuery({
    queryKey: ['patient', patientId, 'cgm', 'devices'] as const,
    queryFn: () => cgmApi.devices(patientId),
    enabled: opts.enabled ?? true,
  });

/**
 * Per-patient glucose target ranges. Reads from the patient app-data
 * store (where v1 also persists overrides) and merges with the default
 * ranges to produce a fully-resolved `GlucoseTargets` object.
 *
 * Returns the resolved targets plus a flag indicating whether the patient
 * has any overrides (so the UI can show a "reset" affordance only when
 * there's something to reset).
 */
const GLUCOSE_RANGES_KEY = 'glucose-ranges-mmol-upper';

export const useGlucoseRanges = (patientId: number) => {
  const query = useQuery({
    queryKey: ['patient', patientId, 'app-data', 'cgm', GLUCOSE_RANGES_KEY] as const,
    queryFn: () =>
      patientAppDataApi.get<StoredGlucoseRanges>(patientId, 'general', 'cgm', GLUCOSE_RANGES_KEY),
    staleTime: 60 * 60 * 1000,
  });
  const overrides = query.data?.value ?? null;
  const targets: GlucoseTargets = resolveGlucoseTargets(overrides);
  const hasOverrides = !!overrides && Object.keys(overrides).length > 0;
  return { ...query, targets, hasOverrides };
};

export const useUpdateGlucoseRanges = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (next: GlucoseTargets) =>
      patientAppDataApi.put(patientId, 'general', 'cgm', GLUCOSE_RANGES_KEY, toStored(next)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patient', patientId, 'app-data', 'cgm', GLUCOSE_RANGES_KEY] });
    },
  });
};

export const useResetGlucoseRanges = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      patientAppDataApi.delete(patientId, 'general', 'cgm', GLUCOSE_RANGES_KEY),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patient', patientId, 'app-data', 'cgm', GLUCOSE_RANGES_KEY] });
    },
  });
};

export const useCgmDays = (patientId: number) =>
  useQuery({ queryKey: queryKeys.cgmDays(patientId), queryFn: () => cgmApi.days(patientId) });

/* ────────────────────────── ADVISOR CHAT (RxAssistant) ──────────────────── */

/**
 * Resolve a session by id, OR by (subjectType, subjectId), OR fall through
 * to no-session (the Welcome state). Same conditional v1 uses
 * (clinician/src/services/AdvisorChatService.ts:193).
 */
export const useAdvisorChatSession = (
  patientId: number | null | undefined,
  args: {
    sessionId?: number | null;
    subjectType?: import('@/api/advisorChat').AdvisorChatSubjectType;
    subjectId?: string;
  },
) => {
  const enabled =
    !!patientId && (!!args.sessionId || (!!args.subjectType && !!args.subjectId));
  return useQuery({
    queryKey: [
      'advisor-chat',
      patientId ?? 0,
      'session',
      args.sessionId ?? null,
      args.subjectType ?? null,
      args.subjectId ?? null,
    ] as const,
    queryFn: async () => {
      if (args.sessionId) {
        return advisorChatApi.getSession({ patientId: patientId!, sessionId: args.sessionId });
      }
      if (args.subjectType && args.subjectId) {
        return advisorChatApi.getSessionBySubject({
          patientId: patientId!,
          subjectType: args.subjectType,
          subjectId: args.subjectId,
        });
      }
      return null;
    },
    enabled,
  });
};

export const useAdvisorChatSessions = (
  patientId: number | null | undefined,
  args?: { subjectType?: import('@/api/advisorChat').AdvisorChatSubjectType },
) =>
  useQuery({
    queryKey: ['advisor-chat', patientId ?? 0, 'sessions', args?.subjectType ?? null] as const,
    queryFn: () =>
      advisorChatApi.getSessions({ patientId: patientId!, subjectType: args?.subjectType }),
    enabled: !!patientId,
  });

/**
 * Send a message. Single mutation that handles both code paths: when no
 * `sessionId` is given it POSTs to `.../sessions` (creating + sending the
 * first message in one call); otherwise it POSTs to the existing session.
 *
 * The optimistic-add-then-replace pattern lives in the UI layer (matches
 * v1's handling) — this hook only does the network call so consumers can
 * compose their own optimistic state.
 */
export const useSendAdvisorMessage = (patientId: number | null | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      sessionId?: number | null;
      content: string;
      subjectType?: import('@/api/advisorChat').AdvisorChatSubjectType;
      subjectId?: string;
      context?: import('@/api/advisorChat').ChatContext;
    }) => {
      if (args.sessionId) {
        return advisorChatApi.sendMessage({
          patientId: patientId!,
          sessionId: args.sessionId,
          content: args.content,
        });
      }
      return advisorChatApi.createSession({
        patientId: patientId!,
        initialMessage: args.content,
        subjectType: args.subjectType ?? 'general',
        subjectId: args.subjectId,
        context: args.context,
      });
    },
    onSuccess: () => {
      // Session list rolls forward (new updated_time + maybe a new session).
      qc.invalidateQueries({ queryKey: ['advisor-chat', patientId ?? 0, 'sessions'] });
    },
  });
};

export const useLoadMoreAdvisorMessages = (
  patientId: number | null | undefined,
  args: {
    sessionId?: number | null;
    subjectType?: import('@/api/advisorChat').AdvisorChatSubjectType;
    subjectId?: string;
  },
) =>
  useMutation({
    mutationFn: (nextTimestamp: string) => {
      if (args.sessionId) {
        return advisorChatApi.getSession({
          patientId: patientId!,
          sessionId: args.sessionId,
          messageLimit: 10,
          messageNextTimestamp: nextTimestamp,
        });
      }
      if (args.subjectType && args.subjectId) {
        return advisorChatApi.getSessionBySubject({
          patientId: patientId!,
          subjectType: args.subjectType,
          subjectId: args.subjectId,
          messageLimit: 10,
          messageNextTimestamp: nextTimestamp,
        });
      }
      return Promise.resolve(null);
    },
  });

/**
 * Daily server-suggested questions for the welcome state. Server caches
 * per-day, so we set a long stale time (matches v1's 15min default).
 */
export const useAdvisorQuestionSuggestions = (
  patientId: number | null | undefined,
  args: {
    subjectType?: import('@/api/advisorChat').AdvisorChatSubjectType;
    subjectId?: string;
  } = {},
) => {
  const today = new Date().toISOString().slice(0, 10);
  return useQuery({
    queryKey: [
      'advisor-chat',
      patientId ?? 0,
      'question-suggestions',
      today,
      args.subjectType ?? null,
      args.subjectId ?? null,
    ] as const,
    queryFn: () =>
      advisorChatApi.getQuestionSuggestions({
        patientId: patientId!,
        questionDate: today,
        subjectType: args.subjectType,
        subjectId: args.subjectId,
      }),
    enabled: !!patientId,
    staleTime: 15 * 60 * 1000, // 15 min
  });
};

/* -------------------- PATIENT NOTES -------------------- */

/**
 * Shared care-team note history for a patient (newest first).
 */
export const usePatientNotes = (patientId: number | null | undefined) =>
  useQuery({
    queryKey: queryKeys.patientNotes(patientId ?? 0),
    queryFn: () => patientNotesApi.list(patientId!),
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000,
  });

export const useCreatePatientNote = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (note: string) => patientNotesApi.create(patientId, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.patientNotes(patientId) });
    },
  });
};

export const useUpdatePatientNote = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId, note }: { noteId: number; note: string }) =>
      patientNotesApi.update(patientId, noteId, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.patientNotes(patientId) });
    },
  });
};

/* -------------------- PATIENT DOCUMENTS (FOOD-1380) -------------------- */

export const usePatientDocuments = (patientId: number | null | undefined) =>
  useQuery({
    queryKey: queryKeys.patientDocuments(patientId ?? 0),
    queryFn: () => patientDocumentsApi.list(patientId!),
    enabled: !!patientId,
  });

export const useUploadPatientDocument = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof patientDocumentsApi.upload>[1]) =>
      patientDocumentsApi.upload(patientId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.patientDocuments(patientId) });
    },
  });
};

export const usePatchPatientDocument = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      documentId,
      body,
    }: {
      documentId: number;
      body: PatchPatientDocumentInput;
    }) => patientDocumentsApi.patch(patientId, documentId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.patientDocuments(patientId) });
    },
  });
};

/**
 * Notify the patient about a shared document [FOOD-1593].
 *
 * No invalidation: sending mail changes nothing we cache. (Matches
 * `useSendReportLink`.) MVP does not display a last-notified time, so there
 * is no document field to refresh either.
 */
export const useNotifyPatientDocument = (patientId: number) =>
  useMutation({
    mutationFn: (documentId: number) =>
      patientDocumentsApi.notify(patientId, documentId),
  });

/**
 * A shared document's bytes, as a Blob.
 *
 * Returns the Blob, not an object URL: the URL's lifetime belongs to whatever
 * renders it, via `useObjectUrl`. The content endpoint serves
 * `Content-Disposition: attachment`, so an `<object data>` pointed at the URL
 * would download instead of render — the bytes have to come through the
 * session as a blob. [FOOD-1594]
 */
export const usePatientDocumentBlob = (
  patientId: number | null | undefined,
  documentId: number | null | undefined,
) =>
  useQuery({
    queryKey: ['patient', patientId ?? 0, 'documents', documentId ?? 0, 'content'] as const,
    queryFn: () => patientDocumentsApi.contentBlob(patientId!, documentId!),
    enabled: !!patientId && !!documentId,
    // The bytes are immutable for a given document id.
    staleTime: 60 * 60 * 1000,
  });

export const useDeletePatientDocument = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: number) => patientDocumentsApi.remove(patientId, documentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.patientDocuments(patientId) });
    },
  });
};

/* -------------------- CLINICIAN TIPS -------------------- */

/**
 * Tips this clinic has sent to a single patient. Defaults to the last
 * 12 months — matches v1's `sendTip` history list. The fixed-window
 * default is intentional: clinicians rarely need older tips, and a
 * large unbounded fetch would balloon the response on long-tenured
 * patients.
 */
export const usePatientClinicianTips = (
  patientId: number | null | undefined,
  opts: { months?: number } = {},
) => {
  const months = opts.months ?? 12;
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - months);
  const dateUntil = to.toISOString().slice(0, 10);
  const dateSince = from.toISOString().slice(0, 10);
  return useQuery({
    queryKey: ['patient', patientId ?? 0, 'clinician-tips', dateSince, dateUntil] as const,
    queryFn: () =>
      clinicianTipsApi.list({ patientId: patientId!, dateSince, dateUntil }),
    enabled: !!patientId,
  });
};

export const useSendClinicianTip = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      body: CreateClinicianTipBatchRequest;
      includeSms?: boolean;
    }) => clinicianTipsApi.sendBatch(args.body, { includeSms: args.includeSms }),
    onSuccess: (_data, args) => {
      // Invalidate per-patient histories so the modal's "History" list
      // refreshes after a send. Single-patient send is the common path
      // but we iterate so multi-recipient sends invalidate all of them.
      for (const pid of args.body.patient_ids) {
        qc.invalidateQueries({ queryKey: ['patient', pid, 'clinician-tips'] });
      }
    },
  });
};

/* -------------------- CLINICIAN LINKS -------------------- */

/** Send a URL to one or more patients. No history endpoint in V2 yet —
 *  v1 doesn't fetch link history on the patient page either, so this
 *  hook is fire-and-forget. */
export const useSendClinicianLink = () =>
  useMutation({
    mutationFn: (body: CreateClinicianLinkBatchRequest) =>
      clinicianLinksApi.sendBatch(body),
  });

/* -------------------- CLINICIAN MEAL FEEDBACK -------------------- */

/**
 * Submit clinician feedback on a patient's meal. Write-only — MNT
 * telemetry has no read endpoint, and patient-feedback consumers
 * silently ignore rows whose telemetry `user_id` != `patient_id`. See
 * `api/telemetry.ts` for the attribution caveat in full.
 *
 * The UI keeps its own per-session memory of "what I sent" via
 * `useClinicianMealFeedbackStore` (localStorage) so the thumbs+comment
 * survive page reloads even without a server read.
 */
export const useSubmitClinicianMealFeedback = () =>
  useMutation({
    mutationFn: (input: ClinicianMealFeedbackInput) =>
      telemetryApi.submitClinicianMealFeedback(input),
  });

/** Submit clinician feedback on the new UI (telemetry event). */
export const useSubmitClinicianUiFeedback = () =>
  useMutation({
    mutationFn: (input: ClinicianUiFeedbackInput) =>
      telemetryApi.submitClinicianUiFeedback(input),
  });

/* -------------------------------------------------------------------------- */
/*                                Water                                       */
/* -------------------------------------------------------------------------- */

/** Helper — start of the month for a YYYY-MM-DD date string. Keyed off
 *  the first day of the month so all WaterCupsControls on the same
 *  month share one cache entry (and one network request). */
const monthBounds = (isoDate: string): { since: string; until: string; key: string } => {
  const d = new Date(`${isoDate}T00:00:00`);
  const since = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
  const until = new Date(d.getFullYear(), d.getMonth() + 1, 1).toISOString().slice(0, 10);
  return { since, until, key: since };
};

/** Range fetch — used by the Nutrients tab's water_cups row. */
export const useWaterRange = (
  patientId: number,
  from: string | null | undefined,
  to: string | null | undefined,
) =>
  useQuery({
    queryKey: ['patient', patientId, 'water-range', from ?? null, to ?? null] as const,
    queryFn: () => waterApi.list(patientId, from!, to!),
    enabled: patientId > 0 && !!from && !!to,
    staleTime: 60_000,
  });

/**
 * Fetch a month of water entries containing `isoDate`. Multiple
 * WaterCupsControls within the same month share this cache, so a
 * timeline rendering 14 day-headers fires one request, not 14.
 */
export const useWaterMonth = (patientId: number, isoDate: string | null | undefined) => {
  const bounds = useMemo(() => (isoDate ? monthBounds(isoDate) : null), [isoDate]);
  return useQuery({
    queryKey: ['patient', patientId, 'water-month', bounds?.key ?? null] as const,
    queryFn: () => waterApi.list(patientId, bounds!.since, bounds!.until),
    enabled: bounds !== null && patientId > 0,
    staleTime: 60_000,
  });
};

/**
 * Set the day's cup count. Optimistically patches the month cache for
 * every visible date that shares the month so the +/− buttons feel
 * instant; rolls back on failure.
 */
export const useSetWater = (patientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ date, cups }: { date: string; cups: number }) =>
      waterApi.set(patientId, date, cups),
    onMutate: async ({ date, cups }) => {
      const { key } = monthBounds(date);
      const queryKey = ['patient', patientId, 'water-month', key] as const;
      await qc.cancelQueries({ queryKey });
      const prev = qc.getQueryData<WaterEntry[]>(queryKey);
      qc.setQueryData<WaterEntry[]>(queryKey, (cur) => {
        const list = cur ?? [];
        const idx = list.findIndex((w) => w.water_date === date);
        if (idx >= 0) {
          const next = list.slice();
          next[idx] = { ...next[idx], cups };
          return next;
        }
        return [...list, { water_date: date, patient_id: patientId, cups }].sort((a, b) =>
          a.water_date.localeCompare(b.water_date),
        );
      });
      return { prev, queryKey };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(ctx.queryKey, ctx.prev);
    },
    onSettled: (_data, _err, { date }) => {
      const { key } = monthBounds(date);
      qc.invalidateQueries({ queryKey: ['patient', patientId, 'water-month', key] });
    },
  });
};

/* -------------------------------------------------------------------------- */
/*                       Wellbeing — mindful-eating surveys                   */
/* -------------------------------------------------------------------------- */

/** Form definition for the mindful-eating survey. Rarely changes —
 *  cached for an hour. */
export const useMindfulEatingForm = (patientId: number) =>
  useQuery({
    queryKey: ['user-surveys', patientId, 'form', 'mindful-eating'] as const,
    queryFn: () => userSurveysApi.form(patientId, 'mindful-eating'),
    enabled: patientId > 0,
    staleTime: 60 * 60 * 1000,
  });

/** Single meal's survey response — used by the meal-detail dialog and
 *  any place that surfaces an inline mindful-eating preview. */
export const useMindfulEatingForMeal = (patientId: number, mealId: number | null) =>
  useQuery({
    queryKey: ['user-surveys', patientId, 'response', 'meal', mealId] as const,
    queryFn: () =>
      userSurveysApi.responses(patientId, {
        surveyType: 'mindful-eating',
        relatedActivityType: 'meal-log',
        relatedActivityId: mealId ?? undefined,
      }),
    enabled: patientId > 0 && !!mealId,
  });

/**
 * Page-level paginated fetch of all mindful-eating responses for a
 * patient. The endpoint caps at 100 per request and has no date
 * filter, so we paginate by `before_time` cursors. The page joins the
 * responses against meals fetched for the visible date range and only
 * renders responses whose meal is in range.
 *
 * "Load older" semantics rather than infinite scroll — clinicians
 * usually want recent first; a button is friendlier than auto-fetch.
 */
export const useMindfulEatingResponses = (patientId: number) =>
  useInfiniteQuery({
    queryKey: ['user-surveys', patientId, 'responses', 'mindful-eating'] as const,
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      userSurveysApi.responses(patientId, {
        surveyType: 'mindful-eating',
        relatedActivityType: 'meal-log',
        beforeTime: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    enabled: patientId > 0,
    // Only suggest a next page when the server returned the full 100 —
    // otherwise we've hit the end of the patient's history.
    getNextPageParam: (lastPage) =>
      lastPage.length === 100
        ? lastPage[lastPage.length - 1].created_time
        : undefined,
  });
